import { useState, useEffect } from 'react';
import api, { handleApiError } from '../services/api';
import toast from 'react-hot-toast';
import { Shield, Save, Check, X, Lock, Package, ShoppingCart, CreditCard, Settings, FileText, Database } from 'lucide-react';

const moduleGroups = [
  { id: 'master', label: 'Master', icon: Database,
    permissions: [
      { id: 'view_products', label: 'Lihat Produk' },
      { id: 'create_products', label: 'Tambah Produk' },
      { id: 'edit_products', label: 'Edit Produk' },
      { id: 'delete_products', label: 'Hapus Produk' },
      { id: 'view_categories', label: 'Lihat Kategori' },
      { id: 'create_categories', label: 'Tambah Kategori' },
      { id: 'edit_categories', label: 'Edit Kategori' },
      { id: 'delete_categories', label: 'Hapus Kategori' },
      { id: 'view_units', label: 'Lihat Satuan' },
      { id: 'view_warehouses', label: 'Lihat Gudang' },
    ] },
  { id: 'purchase', label: 'Pembelian', icon: ShoppingCart,
    permissions: [
      { id: 'view_purchases', label: 'Lihat Pembelian' },
      { id: 'create_purchases', label: 'Buat Pembelian' },
      { id: 'edit_purchases', label: 'Edit Pembelian' },
      { id: 'delete_purchases', label: 'Hapus Pembelian' },
      { id: 'approve_purchases', label: 'Setujui Pembelian' },
      { id: 'view_suppliers', label: 'Lihat Supplier' },
      { id: 'create_suppliers', label: 'Tambah Supplier' },
      { id: 'edit_suppliers', label: 'Edit Supplier' },
      { id: 'delete_suppliers', label: 'Hapus Supplier' },
    ] },
  { id: 'sales', label: 'Penjualan', icon: Package,
    permissions: [
      { id: 'view_sales', label: 'Lihat Penjualan' },
      { id: 'create_sales', label: 'Buat Penjualan' },
      { id: 'edit_sales', label: 'Edit Penjualan' },
      { id: 'delete_sales', label: 'Hapus Penjualan' },
      { id: 'view_customers', label: 'Lihat Pelanggan' },
      { id: 'create_customers', label: 'Tambah Pelanggan' },
      { id: 'edit_customers', label: 'Edit Pelanggan' },
      { id: 'delete_customers', label: 'Hapus Pelanggan' },
    ] },
  { id: 'inventory', label: 'Persediaan', icon: Package,
    permissions: [
      { id: 'view_inventory', label: 'Lihat Stok' },
      { id: 'create_stock_in', label: 'Stok Masuk' },
      { id: 'create_stock_out', label: 'Stok Keluar' },
      { id: 'transfer_stock', label: 'Transfer Stok' },
      { id: 'adjust_stock', label: 'Adjust Stok' },
      { id: 'view_stock_opname', label: 'Lihat Opname' },
      { id: 'create_stock_opname', label: 'Buat Opname' },
    ] },
  { id: 'finance', label: 'Keuangan', icon: CreditCard,
    permissions: [
      { id: 'view_accounts', label: 'Lihat Akun' },
      { id: 'create_accounts', label: 'Tambah Akun' },
      { id: 'edit_accounts', label: 'Edit Akun' },
      { id: 'delete_accounts', label: 'Hapus Akun' },
      { id: 'view_journals', label: 'Lihat Jurnal' },
      { id: 'create_journals', label: 'Buat Jurnal' },
      { id: 'edit_journals', label: 'Edit Jurnal' },
      { id: 'delete_journals', label: 'Hapus Jurnal' },
      { id: 'view_reports', label: 'Lihat Laporan' },
      { id: 'view_receivables', label: 'Lihat Piutang' },
      { id: 'view_payables', label: 'Lihat Utang' },
      { id: 'cash_bank', label: 'Kas & Bank' },
    ] },
  { id: 'settings', label: 'Pengaturan', icon: Settings,
    permissions: [
      { id: 'view_settings', label: 'Lihat Pengaturan' },
      { id: 'edit_settings', label: 'Edit Pengaturan' },
      { id: 'view_users', label: 'Lihat User' },
      { id: 'create_users', label: 'Tambah User' },
      { id: 'edit_users', label: 'Edit User' },
      { id: 'delete_users', label: 'Hapus User' },
      { id: 'view_roles', label: 'Lihat Role' },
      { id: 'edit_roles', label: 'Edit Role' },
      { id: 'view_audit_logs', label: 'Lihat Audit Log' },
    ] },
];

function PermissionsSection({ group, selectedPermissions, onChange }) {
  const Icon = group.icon || FileText;

  return (
    <div className="card">
      <div className="card-header flex items-center">
        <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-400 mr-3">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white">{group.label}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">{group.permissions.length} permission{group.permissions.length > 1 ? 's' : ''}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            const allSelected = group.permissions.every((p) => selectedPermissions.includes(p.id));
            if (allSelected) {
              onChange(group.permissions.map((p) => p.id).filter((id) => !selectedPermissions.includes(id)));
            } else {
              const newPerms = [...selectedPermissions];
              group.permissions.forEach((p) => {
                if (!newPerms.includes(p.id)) newPerms.push(p.id);
              });
              onChange(newPerms);
            }
          }}
          className="ml-auto btn btn-sm btn-outline"
        >
          {group.permissions.every((p) => selectedPermissions.includes(p.id)) ? (
            <><X className="h-3.5 w-3.5 mr-1" /> Uncheck All</>
          ) : (
            <><Check className="h-3.5 w-3.5 mr-1" /> Check All</>
          )}
        </button>
      </div>
      <div className="card-body">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {group.permissions.map((perm) => (
            <label
              key={perm.id}
              className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                selectedPermissions.includes(perm.id)
                  ? 'border-primary-300 bg-primary-50 dark:border-primary-600 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
            >
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-gray-300 accent-primary-800 focus:ring-2 focus:ring-[#2563eb] dark:accent-primary-500 dark:focus:ring-[#60a5fa]"
                checked={selectedPermissions.includes(perm.id)}
                onChange={() => {
                  if (selectedPermissions.includes(perm.id)) {
                    onChange(selectedPermissions.filter((id) => id !== perm.id));
                  } else {
                    onChange([...selectedPermissions, perm.id]);
                  }
                }}
              />
              <span className="ml-3 text-sm text-gray-700 dark:text-gray-200">{perm.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await api.get('/roles');
      if (res.data.success) {
        setRoles(res.data.data || []);
        if (res.data.data?.length > 0 && !selectedRoleId) {
          setSelectedRoleId(res.data.data[0].id);
        }
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedRoleId) {
      const role = roles.find((r) => r.id === selectedRoleId);
      if (role) {
        setSelectedPermissions(role.permissions?.map((p) => p.id || p) || []);
      }
    }
  }, [selectedRoleId, roles]);

  const selectedRole = roles.find((r) => r.id === selectedRoleId);

  const handleSavePermissions = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/roles/${selectedRoleId}/permissions`, {
        permissions: selectedPermissions,
      });
      if (res.data.success) {
        toast.success('Permission berhasil disimpan');
        fetchRoles();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <div className="skeleton h-8 w-48 mb-2" />
          <div className="skeleton h-4 w-64" />
        </div>
        <div className="flex gap-4">
          <div className="skeleton h-10 w-32" />
          <div className="skeleton h-10 w-32" />
          <div className="skeleton h-10 w-32" />
        </div>
        {[...Array(4)].map((_, i) => (
          <div key={i} className="card"><div className="card-body"><div className="skeleton h-16 w-full" /></div></div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Roles & Permissions</h1>
          <p className="page-subtitle">Kelola hak akses setiap role dalam sistem</p>
        </div>
      </div>

      {/* Role Selector */}
      <div className="flex flex-wrap gap-3">
        {roles.map((role) => (
          <button
            key={role.id}
            onClick={() => setSelectedRoleId(role.id)}
            className={`px-5 py-2.5 rounded-xl border text-sm font-medium transition-all flex items-center gap-2 ${
              selectedRoleId === role.id
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-900 dark:text-primary-400 shadow-sm'
                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
            }`}
          >
            <Shield className="h-4 w-4" />
            {role.label || role.name}
          </button>
        ))}
      </div>

      {/* Permissions for selected role */}
      {selectedRole && (
        <>
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <div className="flex items-center">
                <Lock className="h-5 w-5 mr-2 text-primary-500" />
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Permission: {selectedRole.label || selectedRole.name}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {selectedPermissions.length} dari {moduleGroups.reduce((acc, g) => acc + g.permissions.length, 0)} permission dipilih
                  </p>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/30 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    <strong className="text-gray-900 dark:text-white">{selectedRole.users_count || 0}</strong> user(s) dengan role ini
                  </span>
                  <span className="text-gray-500 dark:text-gray-400">
                    {((selectedPermissions.length / moduleGroups.reduce((acc, g) => acc + g.permissions.length, 0)) * 100).toFixed(0)}% permissions aktif
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {moduleGroups.map((group) => (
              <PermissionsSection
                key={group.id}
                group={group}
                selectedPermissions={selectedPermissions}
                onChange={setSelectedPermissions}
              />
            ))}
          </div>

          <div className="flex justify-end">
            <button onClick={handleSavePermissions} disabled={saving} className="btn-primary">
              {saving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Menyimpan...
                </>
              ) : (
                <><Save className="h-4 w-4 mr-2" /> Simpan Permissions</>
              )}
            </button>
          </div>
        </>
      )}

      {!selectedRole && !loading && (
        <div className="card py-16 text-center">
          <Shield className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Role</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada role yang tersedia. Buat role terlebih dahulu.</p>
        </div>
      )}
    </div>
  );
}
